import { ITranscription, ITranscriptionSegment } from '@/types/task.interface'

import {
	TColoredHighlightRange,
	THighlightedTextPart
} from './getHighlightedText'

type TTextSegmentRange = {
	start: number
	end: number
	textStart: number
	textEnd: number
	content: string
}

export const getChunkTextSegments = (
	segments: ITranscriptionSegment[]
): TTextSegmentRange[] => {
	let cursor = 0

	return segments.map((segment, index) => {
		const content = index === 0 ? segment.content.trimStart() : segment.content
		const textStart = cursor
		const textEnd = cursor + content.length

		cursor = textEnd

		return {
			start: segment.start,
			end: segment.end,
			textStart,
			textEnd,
			content
		}
	})
}

export const getChunkText = (chunk: ITranscription) => {
	return getChunkTextSegments(chunk.segments)
		.map(segment => segment.content)
		.join('')
}

const appendPart = (
	parts: THighlightedTextPart[],
	text: string,
	highlight?: Pick<THighlightedTextPart, 'color' | 'type'>
) => {
	if (!text) return

	const lastPart = parts[parts.length - 1]

	if (
		lastPart &&
		lastPart.color === highlight?.color &&
		lastPart.type === highlight?.type
	) {
		lastPart.text += text
		return
	}

	parts.push({
		text,
		color: highlight?.color,
		type: highlight?.type
	})
}

const isHighlightIntersectingSegment = (
	highlight: TColoredHighlightRange,
	segment: TTextSegmentRange
) => {
	const isZeroDurationSegment = segment.start === segment.end

	if (highlight.type === 'keyword' && isZeroDurationSegment) {
		return segment.start >= highlight.start && segment.start <= highlight.end
	}

	return highlight.start < segment.end && highlight.end > segment.start
}

export const getHighlightedChunkText = (
	chunk: ITranscription,
	sortedHighlights: TColoredHighlightRange[]
): THighlightedTextPart[] => {
	const textSegments = getChunkTextSegments(chunk.segments)

	if (!sortedHighlights.length) {
		return [{ text: textSegments.map(segment => segment.content).join('') }]
	}

	const chunkHighlights = sortedHighlights.filter(
		highlight => highlight.start < chunk.end && highlight.end > chunk.start
	)

	if (!chunkHighlights.length) {
		return [{ text: textSegments.map(segment => segment.content).join('') }]
	}

	const parts: THighlightedTextPart[] = []

	for (const segment of textSegments) {
		const segmentHighlights = chunkHighlights.filter(highlight =>
			isHighlightIntersectingSegment(highlight, segment)
		)

		if (!segmentHighlights.length) {
			appendPart(parts, segment.content)
			continue
		}

		const segmentDuration = segment.end - segment.start

		if (segmentDuration <= 0) {
			const activeHighlight = segmentHighlights.sort(
				(a, b) => (b.priority || 0) - (a.priority || 0)
			)[0]

			appendPart(parts, segment.content, activeHighlight)
			continue
		}

		const boundaries = [
			segment.start,
			segment.end,
			...segmentHighlights.flatMap(highlight => [
				Math.max(highlight.start, segment.start),
				Math.min(highlight.end, segment.end)
			])
		]
			.filter((value, index, array) => {
				return (
					value >= segment.start &&
					value <= segment.end &&
					array.indexOf(value) === index
				)
			})
			.sort((a, b) => a - b)

		const segmentParts: THighlightedTextPart[] = []

		for (let i = 0; i < boundaries.length - 1; i++) {
			const partStart = boundaries[i]
			const partEnd = boundaries[i + 1]

			if (partEnd <= partStart) continue

			const activeHighlight = segmentHighlights
				.filter(
					highlight => highlight.start < partEnd && highlight.end > partStart
				)
				.sort((a, b) => (b.priority || 0) - (a.priority || 0))[0]

			if (activeHighlight?.type === 'keyword') {
				// A keyword colors the entire segment, replacing any prefix
				// already produced for this segment only.
				segmentParts.length = 0
				appendPart(segmentParts, segment.content, activeHighlight)
				break
			}

			// Adjacent time ranges must share the same character boundary.
			const charStart = Math.round(
				((partStart - segment.start) / segmentDuration) * segment.content.length
			)

			const charEnd = Math.round(
				((partEnd - segment.start) / segmentDuration) * segment.content.length
			)

			appendPart(
				segmentParts,
				segment.content.slice(charStart, charEnd),
				activeHighlight
			)
		}

		for (const part of segmentParts) {
			appendPart(parts, part.text, part)
		}
	}

	return parts.length ? parts : [{ text: getChunkText(chunk) }]
}

export const getSelectedSegmentFromTextOffsets = ({
	chunk,
	from,
	to
}: {
	chunk: ITranscription
	from: number
	to: number
}) => {
	const textSegments = getChunkTextSegments(chunk.segments)

	return textSegments.filter(
		segment => segment.textEnd > from && segment.textStart < to
	)
}
